import { useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { Upsell } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import type {
  CommunityTemplateGalleryRead,
  CommunityTemplateRead,
  TemplatePackRead,
} from '../../types/api';
import { CommunityGallery } from '../templates/CommunityGallery';
import { COMMUNITY_TEMPLATE_NEW_PATH } from '../templates/communityTemplatePrefill';
import type { BrandPalette } from '../templates/communityZones';
import { PackGallery } from '../templates/PackGallery';
import { useCommunityGallery } from '../templates/useCommunityGallery';
import { useStartFromOwnTemplate } from '../templates/useStartFromOwnTemplate';
import { useTemplatePacks } from '../templates/useTemplatePacks';
import { YourCommunityTemplates } from '../templates/YourCommunityTemplates';

/** The three places a template comes from, in the order the picker lists them. */
const TABS = [
  { key: 'yours', label: 'Yours' },
  { key: 'packs', label: 'Layouts' },
  { key: 'community', label: 'Community' },
] as const;

type PickerTab = (typeof TABS)[number]['key'];

/** The business's own templates, as the brief already holds them. */
export interface OwnTemplates {
  items: readonly CommunityTemplateRead[];
  isLoading: boolean;
  error: string | null;
}

export interface TemplatePickerDialogProps {
  own: OwnTemplates;
  palette: BrandPalette;
  /** True when the brief already carries a template, so switching says what it keeps. */
  hasPinned: boolean;
  onPickPack: (pack: TemplatePackRead) => void;
  onPickCommunity: (template: CommunityTemplateGalleryRead) => void;
  onDismiss: () => void;
}

/** "Nothing here yet" for a tab, with the one way to make something to put in it. */
function EmptyTab({ text }: { text: string }) {
  // Making one is Private templates; without it the way forward is the plan.
  const { can } = usePlan();
  return (
    <div className="kb-section__empty">
      <p className="kb-section__empty-text">{text}</p>
      {can('private_templates') ? (
        <Link className="button" to={COMMUNITY_TEMPLATE_NEW_PATH}>
          Create one
        </Link>
      ) : (
        <Upsell feature="private_templates" />
      )}
    </div>
  );
}

/** Own templates in every status: a business may post on its own design at once. */
function YoursPanel({
  own,
  palette,
  onPick,
}: {
  own: OwnTemplates;
  palette: BrandPalette;
  onPick: (template: CommunityTemplateGalleryRead) => void;
}) {
  // The same signed pin the gallery hands over, delivered here instead of
  // through a navigation.
  const { start, startingId } = useStartFromOwnTemplate({ onPin: onPick });

  if (!own.isLoading && !own.error && own.items.length === 0) {
    return (
      <EmptyTab text="You have not made a template yet. Upload a design or build one, mark where the text goes, and post on it straight away." />
    );
  }
  return (
    <YourCommunityTemplates
      templates={own.items}
      isLoading={own.isLoading}
      error={own.error}
      onUse={(template) => void start(template)}
      startingId={startingId}
      palette={palette}
      showHeading={false}
    />
  );
}

/** The fixed layouts the deployment ships. */
function PacksPanel({ onPick }: { onPick: (pack: TemplatePackRead) => void }) {
  const { packs, isLoading, error } = useTemplatePacks();

  if (error) {
    return (
      <p className="form__error" role="alert">
        {error}
      </p>
    );
  }
  if (isLoading && packs.length === 0) {
    return (
      <p className="kb-section__status" role="status">
        Loading the layouts
      </p>
    );
  }
  if (packs.length === 0) {
    return (
      <EmptyTab text="No fixed layouts added yet. You can still make a design of your own." />
    );
  }
  return <PackGallery packs={packs} onUse={onPick} />;
}

/**
 * Approved shared designs. An own template that is approved is left out, as on
 * `/templates`: it is already under Yours, and listing it twice reads as two.
 */
function CommunityPanel({
  own,
  palette,
  onPick,
}: {
  own: OwnTemplates;
  palette: BrandPalette;
  onPick: (template: CommunityTemplateGalleryRead) => void;
}) {
  const { templates, isLoading, error } = useCommunityGallery();
  const ownIds = new Set(own.items.map((item) => item.id));
  const shared = templates.filter((template) => !ownIds.has(template.id));

  return (
    <CommunityGallery
      templates={shared}
      isLoading={isLoading}
      error={error}
      onUse={onPick}
      palette={palette}
      hasOwn={own.items.length > 0}
    />
  );
}

/**
 * The brief's template picker: the three sources `/templates` offers, in one
 * dialog, so a template can be pinned without leaving a half-written brief.
 *
 * Picking pins in place through the same setters a hand-off from the gallery
 * uses. The tabs are the app's tab pattern — a `tablist` with arrow keys and
 * Home/End on a roving `tabIndex` — and each panel is mounted on first visit
 * and kept, so switching back does not refetch. Escape closes, from `Modal`.
 */
export function TemplatePickerDialog({
  own,
  palette,
  hasPinned,
  onPickPack,
  onPickCommunity,
  onDismiss,
}: TemplatePickerDialogProps) {
  // Null until the user picks a tab: the default follows the data, Yours when
  // the business has anything of its own (or is still finding out), else Layouts.
  const [chosen, setChosen] = useState<PickerTab | null>(null);
  const [visited, setVisited] = useState<readonly PickerTab[]>([]);
  const tabRefs = useRef<Partial<Record<PickerTab, HTMLButtonElement | null>>>({});

  const tab: PickerTab = chosen ?? (own.items.length > 0 || own.isLoading ? 'yours' : 'packs');
  const mounted = visited.includes(tab) ? visited : [...visited, tab];

  function openTab(next: PickerTab) {
    setChosen(next);
    setVisited((current) => {
      const withCurrent = current.includes(tab) ? current : [...current, tab];
      return withCurrent.includes(next) ? withCurrent : [...withCurrent, next];
    });
  }

  /** Arrow keys move between tabs, Home/End to the ends. Roving focus. */
  function handleTabKeys(event: KeyboardEvent<HTMLDivElement>) {
    const index = TABS.findIndex((entry) => entry.key === tab);
    if (index < 0) return;

    let nextIndex: number;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TABS.length - 1;
    else return;

    event.preventDefault();
    const next = TABS[nextIndex];
    if (!next) return;
    openTab(next.key);
    tabRefs.current[next.key]?.focus();
  }

  return (
    <Modal title="Templates" kind="template" size="wide" onDismiss={onDismiss}>
      <div className="template-picker">
        {hasPinned ? (
          <p className="template-picker__note">
            Switching keeps your topic, link and direction. The fields of the current template start
            over.
          </p>
        ) : null}

        <div
          className="tabs template-picker__tabs"
          role="tablist"
          aria-label="Where the template comes from"
          onKeyDown={handleTabKeys}
        >
          {TABS.map((entry) => {
            const isOpen = entry.key === tab;
            return (
              <button
                className={isOpen ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                key={entry.key}
                type="button"
                role="tab"
                id={`template-picker-tab-${entry.key}`}
                aria-selected={isOpen}
                aria-controls={`template-picker-panel-${entry.key}`}
                tabIndex={isOpen ? 0 : -1}
                ref={(node) => {
                  tabRefs.current[entry.key] = node;
                }}
                onClick={() => openTab(entry.key)}
              >
                {entry.label}
                {entry.key === 'yours' && own.items.length > 0 ? (
                  <span className="template-picker__count">{own.items.length}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        {TABS.map((entry) => {
          if (!mounted.includes(entry.key)) return null;
          return (
            <div
              className="tabs__panel template-picker__panel"
              key={entry.key}
              role="tabpanel"
              id={`template-picker-panel-${entry.key}`}
              aria-labelledby={`template-picker-tab-${entry.key}`}
              hidden={entry.key !== tab}
            >
              {entry.key === 'yours' ? (
                <YoursPanel own={own} palette={palette} onPick={onPickCommunity} />
              ) : entry.key === 'packs' ? (
                <PacksPanel onPick={onPickPack} />
              ) : (
                <CommunityPanel own={own} palette={palette} onPick={onPickCommunity} />
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
